<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import WhatsappFlowsAPI from 'dashboard/api/whatsappFlows';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import WhatsappFlowEditor from './WhatsappFlowEditor.vue';
import FlowIssuesDialog from './FlowIssuesDialog.vue';
import { metaIssueItems } from './flowIssues';

const props = defineProps({
  initialInboxId: { type: [String, Number], default: '' },
  autoCreate: { type: Boolean, default: false },
});

const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');

const flows = ref([]);
const canManage = ref(false);
const isLoading = ref(false);
const isSyncing = ref(false);
const isSaving = ref(false);
const busyFlowId = ref(null);
const selectedInboxId = ref(String(props.initialInboxId || ''));
const pendingAction = ref(null);
const editorRef = ref(null);
const confirmRef = ref(null);
const issuesRef = ref(null);

const STATUS_CLASSES = {
  DRAFT: 'bg-n-slate-3 text-n-slate-12',
  PUBLISHED: 'bg-n-teal-3 text-n-teal-11',
  DEPRECATED: 'bg-n-amber-3 text-n-amber-11',
  BLOCKED: 'bg-n-ruby-3 text-n-ruby-11',
  THROTTLED: 'bg-n-amber-3 text-n-amber-11',
};

const cloudInboxes = computed(() =>
  (inboxes.value || []).filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
  )
);

const inboxOptions = computed(() =>
  cloudInboxes.value.map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  }))
);

const filterOptions = computed(() => [
  { value: '', label: t('WHATSAPP_FLOWS.ALL_INBOXES') },
  ...inboxOptions.value,
]);

const visibleFlows = computed(() =>
  selectedInboxId.value
    ? flows.value.filter(
        flow => String(flow.inbox_id) === String(selectedInboxId.value)
      )
    : flows.value
);

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});
const formatDate = value =>
  value ? dateFormatter.format(new Date(value)) : '';

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || t(fallback);

// Erros aparecem no modal do sistema; sucessos seguem no aviso breve (useAlert) do painel.
const showError = (titleKey, error, fallback) =>
  issuesRef.value?.open({
    title: t(titleKey),
    issues: [errorMessage(error, fallback)],
  });

const showMetaIssues = (flow, descriptionKey) =>
  issuesRef.value?.open({
    title: t('WHATSAPP_FLOWS.META_ERRORS.TITLE'),
    description: t(descriptionKey),
    issues: metaIssueItems(flow.validation_errors, t),
  });

const fetchFlows = async () => {
  isLoading.value = true;
  try {
    const { data } = await WhatsappFlowsAPI.list();
    flows.value = data.payload || [];
    canManage.value = !!data.can_manage;
  } catch (error) {
    showError(
      'WHATSAPP_FLOWS.ISSUES.LOAD_ERROR_TITLE',
      error,
      'WHATSAPP_FLOWS.LOAD_ERROR'
    );
  } finally {
    isLoading.value = false;
  }
};

const replaceFlow = updated => {
  const index = flows.value.findIndex(flow => flow.id === updated.id);
  if (index >= 0) flows.value.splice(index, 1, updated);
  else flows.value.unshift(updated);
};

const openCreate = () =>
  editorRef.value?.open({ inboxId: selectedInboxId.value });
const openFlow = flow => editorRef.value?.open({ flow });
const openCopy = flow => editorRef.value?.open({ flow, copy: true });

const saveFlow = async payload => {
  isSaving.value = true;
  const { id, ...attributes } = payload;
  try {
    const { data } = id
      ? await WhatsappFlowsAPI.updateFlow(id, attributes)
      : await WhatsappFlowsAPI.createFlow(attributes);
    replaceFlow(data);
    if (data.validation_errors?.length) {
      editorRef.value?.setFlow(data);
      editorRef.value?.showMetaIssues();
    } else {
      editorRef.value?.close();
      useAlert(t('WHATSAPP_FLOWS.EDITOR.SAVE_SUCCESS'));
    }
  } catch (error) {
    editorRef.value?.setError(
      errorMessage(error, 'WHATSAPP_FLOWS.EDITOR.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const syncFlows = async () => {
  if (!selectedInboxId.value) return;
  isSyncing.value = true;
  try {
    await WhatsappFlowsAPI.sync(selectedInboxId.value);
    await fetchFlows();
    useAlert(t('WHATSAPP_FLOWS.SYNC_SUCCESS'));
  } catch (error) {
    showError(
      'WHATSAPP_FLOWS.ISSUES.SYNC_ERROR_TITLE',
      error,
      'WHATSAPP_FLOWS.ACTION_ERROR'
    );
  } finally {
    isSyncing.value = false;
  }
};

const refreshFlow = async (flow, { silent = false } = {}) => {
  busyFlowId.value = flow.id;
  try {
    const { data } = await WhatsappFlowsAPI.refresh(flow.id);
    replaceFlow(data);
    if (silent) return;
    if (data.warning) {
      issuesRef.value?.open({
        title: t('WHATSAPP_FLOWS.ISSUES.REFRESH_WARNING_TITLE'),
        issues: [data.warning],
      });
    } else {
      useAlert(t('WHATSAPP_FLOWS.REFRESH_SUCCESS'));
    }
  } catch (error) {
    if (!silent) {
      showError(
        'WHATSAPP_FLOWS.ISSUES.ACTION_ERROR_TITLE',
        error,
        'WHATSAPP_FLOWS.ACTION_ERROR'
      );
    }
  } finally {
    busyFlowId.value = null;
  }
};

const openPreview = flow => {
  if (!flow.preview_url?.startsWith('https://')) return;
  window.open(flow.preview_url, '_blank', 'noopener,noreferrer');
};

const copyFlowId = async flow => {
  await copyTextToClipboard(flow.meta_flow_id);
  useAlert(t('WHATSAPP_FLOWS.ACTIONS.ID_COPIED'));
};

const confirmCopy = computed(() => {
  const action = pendingAction.value;
  if (!action) return {};
  const name = action.flow.name;
  if (action.type === 'publish') {
    return {
      title: t('WHATSAPP_FLOWS.PUBLISH.TITLE'),
      description: t('WHATSAPP_FLOWS.PUBLISH.DESCRIPTION', { name }),
      confirm: t('WHATSAPP_FLOWS.PUBLISH.CONFIRM'),
    };
  }
  if (action.type === 'deprecate') {
    return {
      title: t('WHATSAPP_FLOWS.DEPRECATE.TITLE'),
      description: t('WHATSAPP_FLOWS.DEPRECATE.DESCRIPTION', { name }),
      confirm: t('WHATSAPP_FLOWS.DEPRECATE.CONFIRM'),
    };
  }
  return {
    title: t('WHATSAPP_FLOWS.DELETE.TITLE'),
    description:
      action.flow.status === 'DRAFT'
        ? t('WHATSAPP_FLOWS.DELETE.DESCRIPTION_DRAFT', { name })
        : t('WHATSAPP_FLOWS.DELETE.DESCRIPTION_LOCAL', { name }),
    confirm: t('WHATSAPP_FLOWS.DELETE.CONFIRM'),
  };
});

const askAction = (type, flow) => {
  if (type === 'publish' && flow.validation_errors?.length) {
    showMetaIssues(flow, 'WHATSAPP_FLOWS.PUBLISH.BLOCKED');
    return;
  }
  pendingAction.value = { type, flow };
  confirmRef.value?.open();
};

const runPendingAction = async () => {
  const action = pendingAction.value;
  if (!action || busyFlowId.value) return;
  busyFlowId.value = action.flow.id;
  try {
    if (action.type === 'delete') {
      await WhatsappFlowsAPI.delete(action.flow.id);
      flows.value = flows.value.filter(flow => flow.id !== action.flow.id);
      useAlert(t('WHATSAPP_FLOWS.DELETE.SUCCESS'));
    } else {
      const { data } = await WhatsappFlowsAPI[action.type](action.flow.id);
      replaceFlow(data);
      useAlert(
        t(
          action.type === 'publish'
            ? 'WHATSAPP_FLOWS.PUBLISH.SUCCESS'
            : 'WHATSAPP_FLOWS.DEPRECATE.SUCCESS'
        )
      );
    }
    confirmRef.value?.close();
    pendingAction.value = null;
  } catch (error) {
    confirmRef.value?.close();
    showError(
      'WHATSAPP_FLOWS.ISSUES.ACTION_ERROR_TITLE',
      error,
      'WHATSAPP_FLOWS.ACTION_ERROR'
    );
    if (action.type === 'publish') refreshFlow(action.flow, { silent: true });
  } finally {
    busyFlowId.value = null;
  }
};

watch(
  () => props.initialInboxId,
  value => {
    if (value) selectedInboxId.value = String(value);
  }
);

onMounted(async () => {
  await fetchFlows();
  if (props.autoCreate && canManage.value && cloudInboxes.value.length) {
    openCreate();
  }
});

defineExpose({ openCreate, fetchFlows });
</script>

<template>
  <section class="flex flex-col gap-4">
    <div
      class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between"
    >
      <label
        class="flex min-w-60 flex-col gap-1.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('WHATSAPP_FLOWS.INBOX') }}
        <NextSelect
          v-model="selectedInboxId"
          :options="filterOptions"
          class="w-full [&>select]:w-full"
        />
      </label>
      <div v-if="canManage" class="flex flex-wrap gap-2">
        <NextButton
          type="button"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-hugeicons-refresh"
          :label="t('WHATSAPP_FLOWS.SYNC')"
          :disabled="!selectedInboxId"
          :title="selectedInboxId ? undefined : t('WHATSAPP_FLOWS.SYNC_HELP')"
          :is-loading="isSyncing"
          @click="syncFlows"
        />
        <NextButton
          type="button"
          size="sm"
          icon="i-hugeicons-add-01"
          :label="t('WHATSAPP_FLOWS.NEW')"
          :disabled="!cloudInboxes.length"
          @click="openCreate"
        />
      </div>
    </div>

    <p
      v-if="!cloudInboxes.length"
      class="mb-0 rounded-xl bg-n-slate-3 p-3 text-sm text-n-slate-12"
    >
      {{ t('WHATSAPP_FLOWS.NO_CLOUD_INBOX') }}
    </p>
    <p
      v-else-if="!canManage && !isLoading"
      class="mb-0 rounded-xl bg-n-slate-3 p-3 text-sm text-n-slate-12"
    >
      {{ t('WHATSAPP_FLOWS.READ_ONLY') }}
    </p>

    <div
      v-if="isLoading"
      class="flex min-h-40 items-center justify-center text-sm text-n-slate-11"
    >
      {{ t('WHATSAPP_FLOWS.SEND.LOADING') }}
    </div>

    <div
      v-else-if="visibleFlows.length"
      class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
    >
      <article
        v-for="flow in visibleFlows"
        :key="flow.id"
        class="flex min-w-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <header class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h4
              class="mb-1 truncate text-sm font-semibold text-n-slate-12"
              :title="flow.name"
            >
              {{ flow.name }}
            </h4>
            <p class="mb-0 truncate text-xs text-n-slate-11">
              {{ flow.inbox_name }}
            </p>
          </div>
          <span
            class="shrink-0 rounded-md px-2 py-1 text-xs font-medium"
            :class="STATUS_CLASSES[flow.status] || STATUS_CLASSES.DRAFT"
          >
            {{ t(`WHATSAPP_FLOWS.STATUS.${flow.status}`) }}
          </span>
        </header>
        <div class="flex flex-wrap gap-1">
          <span
            v-for="category in flow.categories"
            :key="category"
            class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
          >
            {{ t(`WHATSAPP_FLOWS.CATEGORIES.${category}`) }}
          </span>
        </div>
        <button
          v-if="flow.validation_errors?.length"
          type="button"
          class="flex items-center gap-1.5 self-start text-start text-xs font-medium text-n-amber-11 underline-offset-2 hover:underline"
          @click="showMetaIssues(flow, 'WHATSAPP_FLOWS.META_ERRORS.HELP')"
        >
          <span class="i-hugeicons-alert-circle size-4 shrink-0" />
          {{
            t('WHATSAPP_FLOWS.VALIDATION_COUNT', {
              count: flow.validation_errors.length,
            })
          }}
        </button>
        <p class="mb-0 text-xs text-n-slate-11">
          {{
            t('WHATSAPP_FLOWS.UPDATED_AT', {
              date: formatDate(flow.updated_at),
            })
          }}
        </p>
        <footer
          class="mt-auto flex flex-wrap gap-1 border-t border-n-weak pt-3"
        >
          <NextButton
            type="button"
            size="xs"
            variant="ghost"
            color="slate"
            :icon="
              flow.status === 'DRAFT' && canManage
                ? 'i-hugeicons-pencil-edit-02'
                : 'i-hugeicons-view'
            "
            :label="
              flow.status === 'DRAFT' && canManage
                ? t('WHATSAPP_FLOWS.ACTIONS.EDIT')
                : t('WHATSAPP_FLOWS.ACTIONS.VIEW')
            "
            @click="openFlow(flow)"
          />
          <NextButton
            v-if="flow.preview_url"
            type="button"
            size="xs"
            variant="ghost"
            color="slate"
            icon="i-hugeicons-link-square-02"
            :label="t('WHATSAPP_FLOWS.ACTIONS.PREVIEW')"
            @click="openPreview(flow)"
          />
          <NextButton
            type="button"
            size="xs"
            variant="ghost"
            color="slate"
            icon="i-hugeicons-copy-01"
            :aria-label="t('WHATSAPP_FLOWS.ACTIONS.COPY_ID')"
            :title="t('WHATSAPP_FLOWS.ACTIONS.COPY_ID')"
            @click="copyFlowId(flow)"
          />
          <template v-if="canManage">
            <NextButton
              type="button"
              size="xs"
              variant="ghost"
              color="slate"
              icon="i-hugeicons-refresh"
              :aria-label="t('WHATSAPP_FLOWS.ACTIONS.REFRESH')"
              :title="t('WHATSAPP_FLOWS.ACTIONS.REFRESH')"
              :is-loading="busyFlowId === flow.id"
              @click="refreshFlow(flow)"
            />
            <NextButton
              type="button"
              size="xs"
              variant="ghost"
              color="slate"
              icon="i-hugeicons-add-square"
              :aria-label="t('WHATSAPP_FLOWS.ACTIONS.COPY')"
              :title="t('WHATSAPP_FLOWS.ACTIONS.COPY')"
              @click="openCopy(flow)"
            />
            <NextButton
              v-if="flow.status === 'DRAFT'"
              type="button"
              size="xs"
              variant="ghost"
              color="teal"
              icon="i-hugeicons-sent"
              :label="t('WHATSAPP_FLOWS.ACTIONS.PUBLISH')"
              @click="askAction('publish', flow)"
            />
            <NextButton
              v-if="flow.status === 'PUBLISHED'"
              type="button"
              size="xs"
              variant="ghost"
              color="amber"
              icon="i-hugeicons-archive-02"
              :label="t('WHATSAPP_FLOWS.ACTIONS.DEPRECATE')"
              @click="askAction('deprecate', flow)"
            />
            <NextButton
              v-if="['DRAFT', 'DEPRECATED'].includes(flow.status)"
              type="button"
              size="xs"
              variant="ghost"
              color="ruby"
              icon="i-hugeicons-delete-02"
              :aria-label="t('WHATSAPP_FLOWS.ACTIONS.DELETE')"
              :title="t('WHATSAPP_FLOWS.ACTIONS.DELETE')"
              @click="askAction('delete', flow)"
            />
          </template>
        </footer>
      </article>
    </div>

    <div
      v-else-if="cloudInboxes.length"
      class="flex min-h-48 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-strong bg-n-solid-1 p-6 text-center"
    >
      <span
        class="grid size-12 place-items-center rounded-xl bg-n-teal-3 text-n-teal-11"
      >
        <span class="i-hugeicons-workflow-square-06 size-6" />
      </span>
      <h4 class="mb-0 text-base font-semibold text-n-slate-12">
        {{ t('WHATSAPP_FLOWS.EMPTY_TITLE') }}
      </h4>
      <p class="mb-0 max-w-md text-sm text-n-slate-11">
        {{ t('WHATSAPP_FLOWS.EMPTY_DESCRIPTION') }}
      </p>
      <NextButton
        v-if="canManage"
        type="button"
        icon="i-hugeicons-add-01"
        :label="t('WHATSAPP_FLOWS.NEW')"
        @click="openCreate"
      />
    </div>

    <WhatsappFlowEditor
      ref="editorRef"
      :inbox-options="inboxOptions"
      :is-saving="isSaving"
      @save="saveFlow"
    />

    <Dialog
      ref="confirmRef"
      type="alert"
      :title="confirmCopy.title"
      :description="confirmCopy.description"
      :confirm-button-label="confirmCopy.confirm"
      :is-loading="!!busyFlowId"
      @confirm="runPendingAction"
    />
    <FlowIssuesDialog ref="issuesRef" />
  </section>
</template>
